import * as React from "react"
import {
  BaseFeatures,
  BillingInterval,
  FeaturesSummary,
  MachinePricingTier,
} from "@modules/graphql/types"
import { PlanTag } from "@modules/billing/shared/components/PlanTag"
import { TrialRemainingDays } from "@modules/billing/components/TrialRemainingDays"
import { ThemeCss, Spacer, visuallyHiddenCss, Heading } from "gatsby-interface"
import format from "date-fns/format"
import { useFlags } from "@modules/featureFlags"

const Intervals = {
  [BillingInterval.Monthly]: `month`,
  [BillingInterval.Annual]: `year`,
}

export type PlanDetailsProps = {
  planName?: string
  trialDays?: number | null
  price?: string | null
  nextPayment?: string
  interval?: BillingInterval | null
  intervalCount?: number | null
  isTrial?: boolean
  features?: BaseFeatures | null
  totalFeatures?: FeaturesSummary | null
  tier?: MachinePricingTier | null
}

type PlanDetails = {
  label: string
  plural?: string
  value?: number | string | null
  formatted?: string | null
  text?: boolean
}

function intervalText({
  interval,
  intervalCount,
}: {
  interval: BillingInterval
  intervalCount?: number | null
}) {
  if (intervalCount && intervalCount > 1) {
    return `every ${intervalCount} ${Intervals[interval]}s`
  } else {
    return Intervals[interval]
  }
}

export function PlanDetails({
  planName,
  trialDays,
  price,
  nextPayment,
  interval,
  intervalCount,
  isTrial,
  features,
  totalFeatures,
  tier,
}: PlanDetailsProps) {
  const isFreePlan = tier === MachinePricingTier.Free
  const isEnterprisePlan = tier === MachinePricingTier.Enterprise
  const isLegacyPlan =
    tier !== MachinePricingTier.Free &&
    tier !== MachinePricingTier.Professional &&
    tier !== MachinePricingTier.Agency &&
    tier !== MachinePricingTier.Enterprise
  const { flags } = useFlags()

  const details: PlanDetails[] = [
    {
      label: `member`,
      plural: `members`,
      value: isEnterprisePlan
        ? totalFeatures?.users?.quantity
        : features?.users?.quantity,
    },
    {
      label: `site`,
      plural: `sites`,
      value: features?.sites,
    },
    // fall 22 pricing feature
    {
      label: `max site size`,
      value: isEnterprisePlan
        ? totalFeatures?.totalSiteRoutes?.quantity
        : features?.totalSiteRoutes?.quantity,
      formatted: isEnterprisePlan
        ? totalFeatures?.totalSiteRoutes?.formattedQuantity
        : features?.totalSiteRoutes?.formattedQuantity,
    },
    // end fall 22 pricing feature
    {
      label: `custom domain`,
      plural: `custom domains`,
      value: features?.domains,
    },
    {
      label: `hosting integration`,
      plural: `hosting integrations`,
      value: features?.thirdPartyHostingIntegrations,
    },
    {
      label: `code builds`,
      value: 500,
    },
    // fall 22 pricing feature
    {
      label: `build minutes`,
      value: isEnterprisePlan
        ? totalFeatures?.monthlyBuildMinutes?.quantity
        : features?.monthlyBuildMinutes?.quantity,
      formatted: isEnterprisePlan
        ? totalFeatures?.monthlyBuildMinutes?.formattedQuantity
        : features?.monthlyBuildMinutes?.formattedQuantity,
    },
    // end fall 22 pricing feature
    {
      label: `concurrent build`,
      plural: `concurrent builds`,
      value: isEnterprisePlan
        ? totalFeatures?.concurrency?.quantity
        : features?.concurrency?.quantity,
    },
    {
      label: `builds`,
      value: `Burst Mode`,
    },
    {
      label: `bandwidth`,
      value: isEnterprisePlan
        ? totalFeatures?.bandwidth?.quantity
        : features?.bandwidth?.quantity,
      formatted: isEnterprisePlan
        ? totalFeatures?.bandwidth?.formattedQuantity
        : features?.bandwidth?.formattedQuantity,
    },
    {
      label: `requests`,
      value: isEnterprisePlan
        ? totalFeatures?.requests?.quantity
        : features?.requests?.quantity,
      formatted: isEnterprisePlan
        ? totalFeatures?.requests?.formattedQuantity
        : features?.requests?.formattedQuantity,
    },
    {
      label: `invocations`,
      value: isEnterprisePlan
        ? totalFeatures?.invocations?.quantity
        : features?.invocations?.quantity,
      formatted: isEnterprisePlan
        ? totalFeatures?.invocations?.formattedQuantity
        : features?.invocations?.formattedQuantity,
    },
    {
      label: `image CDN requests`,
      value: isEnterprisePlan
        ? totalFeatures?.imageCdnRequests?.quantity
        : features?.imageCdnRequests?.quantity,
      formatted: isEnterprisePlan
        ? totalFeatures?.imageCdnRequests?.formattedQuantity
        : features?.imageCdnRequests?.formattedQuantity,
    },
    {
      label: `log drains`,
      formatted: `Log drains`,
      text: true,
    },
  ]
    .map(item => {
      if (item.value === -1) {
        item.formatted = `Unlimited`
      }

      if (!item.formatted) {
        item.formatted = String(item.value)
      }

      if (item.plural && item.value && (item.value > 1 || item.value === -1)) {
        item.label = item.plural
      }

      return item
    })
    .filter(item => {
      if (item.label === `builds` && !flags.enableBurstModeToggle) {
        return false
      }

      // if fall 22 pricing flag is on, filter out deprecated feature limits
      // the list includes plural and non-plural labels because it could be
      // both at this point depending upon the plan
      if (
        flags.pricingFall2022 &&
        [
          `hosting integration`,
          `hosting integrations`,
          `request`,
          `requests`,
          `custom domain`,
          `custom domains`,
        ].includes(item.label)
      ) {
        return false
      }

      // if fall 22 pricing flag is off, filter out new feature limits
      if (
        !flags.pricingFall2022 &&
        [`max site size`, `build minutes`].includes(item.label)
      ) {
        return false
      }

      if (item.label === `log drains`) {
        return isEnterprisePlan
      }

      if ([`max site size`, `build minutes`].includes(item.label)) {
        return !isLegacyPlan
      }

      return isFreePlan || item.label !== `code builds`
    })

  return (
    <div css={rootCss}>
      <div css={firstRowCss}>
        <PlanTag name={planName} />
        {price && interval && !isTrial && (
          <React.Fragment>
            {`\u00A0`}⋅{`\u00A0`}
            <strong>
              {price} / {intervalText({ interval, intervalCount })}
            </strong>
          </React.Fragment>
        )}

        {isFreePlan && (
          <React.Fragment>
            {`\u00A0`}⋅{`\u00A0`}
            <strong>$0 / month</strong>
          </React.Fragment>
        )}
        {nextPayment &&
          `${`\u00A0`}⋅${`\u00A0`}Next payment on ${format(
            new Date(nextPayment),
            "MMM d, yyyy"
          )}`}
        {isTrial && (
          <React.Fragment>
            <Spacer size={5} direction="horizontal" />
            <TrialRemainingDays days={trialDays} noTooltip={true} />
          </React.Fragment>
        )}
      </div>
      <div>
        <Heading as="h4" css={visuallyHiddenCss}>
          Plan features
        </Heading>
        <ul css={listCss}>
          {details.map(item => (
            <li key={item.label}>
              <strong>{item.formatted}</strong> {!item.text ? item.label : ``}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/* styles */

const rootCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[1],
  color: theme.colors.grey[80],
})

const firstRowCss: ThemeCss = _ => ({
  display: `flex`,
  alignItems: `center`,
  flexWrap: `wrap`,
})

const listCss: ThemeCss = theme => ({
  borderTop: `1px solid ${theme.colors.standardLine}`,
  listStyle: `none`,
  margin: 0,
  marginTop: theme.space[6],
  paddingTop: theme.space[6],
  display: `grid`,
  gridTemplateColumns: `1fr 1fr`,

  [theme.mediaQueries.desktop]: {
    columnGap: theme.space[10],
    gridTemplateColumns: `max-content 1fr`,
  },
})
